/**
 * useSiteMapSites Hook
 *
 * Loads the site list once on mount. `reload` requests it again (ignored while a request
 * is in flight); when to reload is decided by useSiteMap with sitesReloadPolicy.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { fetchSiteMapSites } from '../api/siteMapApiClient';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import {
  initialSitesState,
  sitesReducer,
  sitesResultToAction,
  type SitesState,
} from './siteMapState';

export interface UseSiteMapSitesResult extends SitesState {
  readonly reload: () => void;
}

export function useSiteMapSites(): UseSiteMapSitesResult {
  const [state, dispatch] = useReducer(sitesReducer, initialSitesState);
  const { getAccessToken } = useAuth();

  // A ref so a new getAccessToken identity does not change `reload`
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Request bookkeeping, confined to this hook instance
  const inFlightRef = useRef(false);
  const mountedRef = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    dispatch({ type: 'requested', at: Date.now() });
    try {
      const result = await fetchSiteMapSites({
        apiHost: getAppConfig().apiHost,
        getAccessToken: getAccessTokenRef.current,
      });
      if (mountedRef.current) dispatch(sitesResultToAction(result, Date.now()));
    } catch (err) {
      if (mountedRef.current) {
        dispatch({
          type: 'failed',
          message: err instanceof Error ? err.message : 'Failed to load the site list',
        });
      }
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  const reload = useCallback((): void => {
    void load();
  }, [load]);

  return useMemo(() => ({ ...state, reload }), [state, reload]);
}
